import { storage } from '@/utils';
import { getUniversityUrl } from './http';
import { AxiosError, AxiosRequestConfig } from 'axios';
import CustomAxios from '@/services/http';

const RequestHandler = {
  send: async <T>(request: AxiosRequestConfig): Promise<T> => {
    try {
      const res = await CustomAxios.request<T>(request);
      if (res) {
        return res.data;
      }
    }
    catch (e: any) {
      return errorHandler(e);
    }
  },
};

const errorHandler = (e: AxiosError<any>) => {
  const error: HttpErrorResponse = {
    status: e.response?.status || 0,
    errorCode: e.response?.data?.code || 0,
    messageFa: e.response?.data?.messageFa || 'خطای نامشخص',
    messageEn: e.response?.data?.messageEn || 'API Failed',
    data: e.response?.data || null,
    hasServerMessage: e.response?.data?.messageFa || false,
    errorObject: e,
  };
  throw error;
};

export type TCreditTransferRule = {
  minCreditTransferAmount: number;
  maxCreditTransferAmount: number;
  messages: string[];
};

export type TRedmineIssueDetailResponse<T> = {
  issue: T;
};

export type TRedmineCreateIssueResponse = {
  issue: { id: number };
};

export type TGetCreditResponse = {
  credit: number;
  subsidyCredit?: number;
};

export type TDailySaleProgramResponse = {
  dailySellPrograms: DailySaleProgramEntity[];
};

type TResponse<T> = HttpResponse<T>;

const getBseURL = (baseURL: string) => {
  switch (baseURL) {
    case 'UNIVERSITY':
      return getUniversityUrl();
    case 'SAMADTest':
      return 'https://ts1.samad.app';
    // case "SAMAD":
    //   return "https://api.samad.app/";
    case 'HELPDESK':
      return (storage.get('UNIVERSITY') as UniversityEntity)?.samad?.help_desk;
    default:
      return 'https://api-v2.samad.app/config/';
  }
};

const mutualConfig = (
  baseURL: string,
  method: string,
  needAuth: boolean = true
): AxiosRequestConfig => {
  return {
    method,
    baseURL: getBseURL(baseURL) as string,
    timeout: 15000,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      ...(needAuth
        ? { authorization: `Bearer ${storage.get('TOKEN')}` }
        : {}),
    },
  };
};

const extendHeaders = (
  config: AxiosRequestConfig,
  headers: { [x: string]: string }
) => {
  config.headers = {
    ...config.headers,
    ...headers,
  } as any;
};

const API = {
  User: {

    getLoginToken: async () => {
      const config: AxiosRequestConfig = {
        url: '/rest/users/me/login-token',
        ...mutualConfig('UNIVERSITY', 'get'),
      };
      return await RequestHandler.send<TResponse<any>>(config);
    },

    getCredit: async () => {
      const config: AxiosRequestConfig = {
        url: '/rest/users/nurture-profiles',
        ...mutualConfig('UNIVERSITY', 'get'),
      };
      return await RequestHandler.send<TResponse<TGetCreditResponse>>(config);
    },

    getCreditTransferRuls: async () => {
      const config: AxiosRequestConfig = {
        url: '/rest/credit-transfers',
        ...mutualConfig('UNIVERSITY', 'get'),
      };
      return await RequestHandler.send<TResponse<TCreditTransferRule>>(config);
    },

    creditTransfer: async (data: CreditTransferRequest) => {
      const config: AxiosRequestConfig = {
        url: '/rest/credit-transfers',
        ...mutualConfig('UNIVERSITY', 'put'),
        data,
      };
      return await RequestHandler.send<TResponse<any>>(config); //TODO: response type
    },
  },

  reserve: {

    getDailySellProgram: async () => {
      const config: AxiosRequestConfig = {
        url: '/rest/daily-sell-programs',
        ...mutualConfig('UNIVERSITY', 'get'),
      };
      return await RequestHandler.send<TResponse<TDailySaleProgramResponse>>(
        config
      );
    },

    getReserveReport: async (weekStartDate?: string) => {
      const config: AxiosRequestConfig = {
        url: `/rest/reserves?weekStartDate=${weekStartDate}`,
        ...mutualConfig('UNIVERSITY', 'get'),
      };
      // return await RequestHandler.send<TResponse<TReserveReport>>(config);
      return await RequestHandler.send<TResponse<any>>(config);
    },

    reserveDailySell: async (programId: number, selectedCount: number) => {
      const config: AxiosRequestConfig = {
        url: `/rest/daily-sell-programs/${programId}/reserve-daily-sell`,
        data: { selectedCount },
        ...mutualConfig('UNIVERSITY', 'put'),
      };
      return await RequestHandler.send<TResponse<any>>(config); //TODO: response type
    },

    reserveProgram: async (programId: number, data: ReserveRequest) => {
      const config: AxiosRequestConfig = {
        url: `/rest/reserves/${programId}/reserve`,
        data,
        ...mutualConfig('UNIVERSITY', 'put'),
      };
      return await RequestHandler.send<TResponse<any>>(config); //TODO: response type
    },

  },

  common: {

    getFeedbackDetail: async (id: number) => {
      const params = new URLSearchParams();
      params.append('key', 'e461770de1c4e1535f268744630c8ff7915e86ba');
      params.append('include', 'journals');
      const config: AxiosRequestConfig = {
        url: `issues/${id}.json`,
        ...mutualConfig('SAMAD', 'get', false),
        params,
      };
      return await RequestHandler.send<
        TRedmineIssueDetailResponse<RedmineIssueEntity>
      >(config);
    },

    replyToFeedback: async (
      id: number,
      feedback: RedmineCreateIssueJournal
    ) => {
      const config: AxiosRequestConfig = {
        url: `issues/${id}.json`,
        ...mutualConfig('SAMAD', 'put', false),
        data: { issue: { ...feedback } },
      };
      extendHeaders(config, {
        'X-Redmine-API-Key': 'e461770de1c4e1535f268744630c8ff7915e86ba',
      });
      return await RequestHandler.send<TRedmineCreateIssueResponse>(config);
    },
  },

};

export default API;
